import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FiX, FiMail, FiLock, FiUser } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import {
  OAUTH_REDIRECT_STORAGE_KEY,
  sanitizeRedirectPath,
} from "../../utils/authRedirect";

const AuthModal = ({
  isOpen,
  onClose,
  initialMode = "signin",
  redirectTo = "/",
}) => {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const sanitizedRedirectTo = sanitizeRedirectPath(redirectTo);

  // Update mode when initialMode changes (when different button is clicked)
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (mode === "signin") {
        const { error } = await signIn(email, password);
        if (error) {
          throw error;
        }
        window.sessionStorage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
        onClose();
        navigate(sanitizedRedirectTo, { replace: true });
      } else {
        const { data, error } = await signUp(email, password, name);
        if (error) {
          throw error;
        } else if (data?.session) {
          window.sessionStorage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
          onClose();
          navigate(sanitizedRedirectTo, { replace: true });
        } else {
          setMessage(
            "Check your email for the confirmation link to complete your registration!"
          );
        }
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);

    try {
      window.sessionStorage.setItem(
        OAUTH_REDIRECT_STORAGE_KEY,
        sanitizedRedirectTo
      );
      const { error } = await signInWithGoogle();
      if (error) {
        throw error;
      }
    } catch (err) {
      window.sessionStorage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
      setError(err.message || "An error occurred");
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-[400px] rounded-xl bg-white p-8 shadow-large">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded text-2xl text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
        >
          <FiX className="size-6" />
        </button>

        {/* Title */}
        <h2 className="mb-6 text-2xl font-semibold text-gray-900">
          {mode === "signin" ? "Sign In" : "Sign Up"}
        </h2>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-600">
            {message}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 pl-11 pr-4 text-base transition-all duration-200 placeholder:text-gray-500 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="Enter your name (optional)"
                />
              </div>
            </div>
          )}

          <div>
            <div className="relative">
              <FiMail className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 pl-11 pr-4 text-base transition-all duration-200 placeholder:text-gray-500 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 pl-11 pr-4 text-base transition-all duration-200 placeholder:text-gray-500 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-lg bg-primary-600 py-3 text-base font-semibold text-white transition-all duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50"
          >
            {loading ? "Loading..." : mode === "signin" ? "Sign In" : "Sign Up"}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-200"></div>
          <span className="px-4 text-sm font-medium text-gray-500">OR</span>
          <div className="flex-1 border-t border-gray-200"></div>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="mb-6 flex w-full items-center justify-center space-x-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
        >
          <FcGoogle className="size-5" />
          <span>Continue with Google</span>
        </button>

        {/* Toggle Mode */}
        <p className="text-center text-sm text-gray-600">
          {mode === "signin" ? "Don't have an account? " : "Have an account? "}
          <button
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError("");
              setMessage("");
            }}
            className="font-medium text-primary-600 underline hover:text-primary-700"
          >
            {mode === "signin" ? "Sign Up" : "Sign In"}
          </button>
        </p>
      </div>
    </div>
  );
};

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialMode: PropTypes.oneOf(["signin", "signup"]),
  redirectTo: PropTypes.string,
};

export default AuthModal;
